import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Search, MapPin, Package, Building2, ChevronRight, Plus, Trash2, Loader2, Globe, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TechDatabaseProperties({ token, role }) {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const isAdmin = role === 'admin';

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const [propRes, platRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setProperties(propRes.data || []);
      setPlatforms(platRes.data || []);
    } catch { toast.error('Failed to load properties'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const fetchPropertyAssignments = useCallback(async (propertyId) => {
    setAssignmentsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/assignments?property_id=${propertyId}`, { headers });
      setAssignments(res.data || []);
    } catch { setAssignments([]); }
    finally { setAssignmentsLoading(false); }
  }, [headers]);

  const selectProperty = (p) => {
    setSelectedProperty(p);
    fetchPropertyAssignments(p.id);
  };

  const removeAssignment = async (assignment) => {
    if (!window.confirm(`Remove ${assignment.product_name || assignment.vendor_name} from this property?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
        headers,
        params: {
          property_id: selectedProperty.id,
          platform_type_id: assignment.platform_type_id,
          product_id: assignment.product_id,
          layer: 'actual',
        },
      });
      toast.success('Assignment removed');
      fetchPropertyAssignments(selectedProperty.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return properties;
    const q = searchQuery.toLowerCase();
    return properties.filter(p => p.name?.toLowerCase().includes(q) || p.id?.toLowerCase().includes(q));
  }, [properties, searchQuery]);

  // Group assignments by platform category
  const groupedAssignments = useMemo(() => {
    const platMap = {};
    platforms.forEach(p => { platMap[p.id] = p; });

    const groups = {};
    assignments.forEach(a => {
      const plat = platMap[a.platform_type_id] || { name: a.platform_type_id, category_id: 'uncategorized' };
      const catId = plat.category_id || 'uncategorized';
      if (!groups[catId]) groups[catId] = { platforms: {} };
      if (!groups[catId].platforms[a.platform_type_id]) {
        groups[catId].platforms[a.platform_type_id] = { name: plat.name || plat.short || a.platform_type_id, items: [] };
      }
      groups[catId].platforms[a.platform_type_id].items.push(a);
    });

    return Object.entries(groups).map(([catId, g]) => ({
      catId,
      platforms: Object.entries(g.platforms).map(([pid, pl]) => ({
        id: pid, name: pl.name, items: pl.items.sort((a, b) => (a.product_name || a.vendor_name || '').localeCompare(b.product_name || b.vendor_name || '')),
      })).sort((a, b) => a.name.localeCompare(b.name)),
    })).sort((a, b) => a.catId.localeCompare(b.catId));
  }, [assignments, platforms]);

  const [sidebarWidth] = useState(320);

  return (
    <div className="flex flex-col h-full overflow-hidden relative" data-testid="tech-database-properties">
      {/* Toolbar */}
      <div className="px-3 md:px-5 py-3 border-b bg-card/50 flex items-center gap-3 shrink-0">
        <div className="relative flex-1 min-w-[150px] max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            data-testid="properties-search"
            className="pl-9 h-9"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex-1" />
        <Badge variant="secondary" className="font-mono text-xs">
          {filtered.length} properties
        </Badge>
      </div>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Property list */}
        <ScrollArea className={`${selectedProperty && typeof window !== 'undefined' && window.innerWidth < 768 ? 'hidden' : ''} w-full md:border-r shrink-0`} style={{ width: typeof window !== 'undefined' && window.innerWidth >= 768 ? `${sidebarWidth}px` : undefined }}>
          <div className="p-3 space-y-0.5">
            {loading ? (
              <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No properties found</p>
            ) : filtered.map(p => (
              <button
                key={p.id}
                onClick={() => selectProperty(p)}
                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors ${
                  selectedProperty?.id === p.id ? 'bg-accent ring-1 ring-ring/20' : 'hover:bg-muted/60'
                }`}
                data-testid={`property-item-${p.id}`}
              >
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{p.type || 'Hotel'} · {p.id}</div>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        </ScrollArea>

        {/* Property detail */}
        <div className="flex-1 overflow-auto min-w-0">
          {!selectedProperty ? (
            <div className="h-full flex items-center justify-center hidden md:flex">
              <div className="text-center">
                <Building2 className="h-10 w-10 text-muted-foreground/25 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Select a property to view its tech stack</p>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="p-4 md:p-6 max-w-4xl space-y-5 animate-fade-in">
                <button className="md:hidden text-xs text-muted-foreground flex items-center gap-1 mb-2" onClick={() => setSelectedProperty(null)}>&larr; Back</button>

                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selectedProperty.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{selectedProperty.type || 'Hotel'}</Badge>
                      <span className="text-xs text-muted-foreground">{assignments.length} solutions deployed</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {assignmentsLoading ? (
                  <div className="flex items-center justify-center h-32"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : assignments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No technology assigned to this property yet.</p>
                ) : (
                  <div className="space-y-4">
                    {groupedAssignments.map(group => (
                      <div key={group.catId}>
                        {group.platforms.map(plat => (
                          <div key={plat.id} className="mb-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Package className="h-3.5 w-3.5 text-muted-foreground" />
                              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{plat.name}</h3>
                              <Badge variant="secondary" className="text-[9px] font-mono">{plat.items.length}</Badge>
                            </div>
                            <div className="space-y-1 ml-5">
                              {plat.items.map((a, i) => (
                                <div key={a.id || i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group text-sm">
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium">{a.product_name || a.vendor_name || 'Unknown'}</span>
                                    {a.vendor_name && a.product_name && a.vendor_name !== a.product_name && (
                                      <span className="text-muted-foreground text-xs ml-1.5">· {a.vendor_name}</span>
                                    )}
                                  </div>
                                  {a.implementation_status && (
                                    <Badge variant="outline" className="text-[9px]">{a.implementation_status}</Badge>
                                  )}
                                  {isAdmin && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0" onClick={() => removeAssignment(a)}>
                                      <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}
